/**
 * bc-product-shipping.js
 *
 * Keeps the free-shipping indicator accurate in real time.
 *
 * Cart-change detection uses the theme's built-in pub/sub system
 * (pubsub.js → subscribe / publish). This is the Shopify-recommended
 * pattern: cart.js (drawer qty/remove) and bc-atc.js both call
 *   publish(PUB_SUB_EVENTS.cartUpdate, { cartData })
 * after every cart mutation, with cartData.total_price already
 * reflecting the new cart total — no extra /cart.js fetch needed.
 *
 * Data contract (bc-product-shipping.liquid):
 *   [data-bc-shipping]   — indicator root
 *   data-threshold       — free-shipping threshold in cents
 *   data-v{variantId}    — that variant's price in cents
 */
(function () {
  'use strict';

  var indicator = document.querySelector('[data-bc-shipping]');
  if (!indicator) return;

  var threshold        = parseInt(indicator.dataset.threshold || '0', 10);
  var currentVarId     = null;
  var currentQty       = 1;
  var currentCartCents = 0;
  var qtyInput         = document.querySelector('[data-bc-main-qty-input]');

  /* ── State logic (mirrors the Liquid calculation) ────────────────────────
     free: cart alone OR this product alone already crosses the threshold.
     unlocks: only the combination crosses it. ──────────────────────────── */
  function calcState(cartCents, price, effectiveCents) {
    if (cartCents >= threshold || price >= threshold) return 'free';
    if (effectiveCents >= threshold)                  return 'unlocks';
    return 'calculated';
  }

  /* ── Swap the visible state block ──────────────────────────────────────── */
  function showState(state) {
    indicator.querySelectorAll('.bc-shipping__state').forEach(function (el) {
      var active = el.classList.contains('bc-shipping__' + state);
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  /* ── Format cents → "$X" (no trailing .00) ────────────────────────────── */
  function formatCents(cents) {
    var dollars = cents / 100;
    return '$' + (Number.isInteger(dollars) ? dollars : dollars.toFixed(2));
  }

  /* ── Update "$X away" span in the unlocks badge ────────────────────────── */
  function updateRemaining(cartCents) {
    var span = indicator.querySelector('.bc-shipping__remaining');
    if (!span) return;
    span.textContent = formatCents(Math.max(0, threshold - cartCents));
  }

  /* ── Recalculate and redraw for the current variant + quantity ─────────── */
  function update() {
    if (!currentVarId) return;
    var price = parseInt(indicator.dataset['v' + currentVarId] || '0', 10);
    var effectiveCents = currentCartCents + (price * currentQty);
    showState(calcState(currentCartCents, price, effectiveCents));
    updateRemaining(currentCartCents);
  }

  /* ── Cart changed: apply new total directly from pub/sub payload ────────── */
  function onCartUpdate(event) {
    var cartData = event && event.cartData;
    if (cartData && typeof cartData.total_price !== 'undefined') {
      currentCartCents = cartData.total_price;
      update();
    }
  }

  /* ── Subscribe to the theme's cart-update pub/sub channel ─────────────── */
  // subscribe() is a global from pubsub.js (loaded by the theme layout).
  // All deferred scripts run in DOM order, so pubsub.js is ready by the time
  // this runs. A short retry covers any edge-case load-order variance.
  function setupSubscription() {
    if (typeof subscribe === 'function') {
      subscribe('cart-update', onCartUpdate);
    } else {
      setTimeout(setupSubscription, 50);
    }
  }
  setupSubscription();

  /* ── Variant pill / select change ─────────────────────────────────────── */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (
      t.classList.contains('product-variant-id') ||
      t.hasAttribute('data-bc-variant-input')
    ) {
      currentVarId = t.value;
      update();
    }
  });

  /* ── Quantity stepper — +/- buttons set .value directly, no change event,
     so listen on the buttons themselves; typing still fires 'change'. ────── */
  if (qtyInput) {
    qtyInput.addEventListener('change', function () {
      currentQty = parseInt(qtyInput.value, 10) || 1;
      update();
    });
  }
  document.addEventListener('click', function (e) {
    if (!qtyInput) return;
    if (e.target.closest('[data-bc-main-qty-plus], [data-bc-main-qty-minus]')) {
      currentQty = parseInt(qtyInput.value, 10) || 1;
      update();
    }
  });

  /* ── Init ───────────────────────────────────────────────────────────────── */
  var initInput = document.querySelector('[data-bc-variant-input]');
  if (initInput) currentVarId = initInput.value;
  if (qtyInput) currentQty = parseInt(qtyInput.value, 10) || 1;

  // Sync cart total with reality. Liquid renders the correct value at SSR,
  // but BFCache / prefetch can serve a stale page — one lightweight fetch
  // on init ensures correctness without touching any event system.
  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      currentCartCents = cart.total_price || 0;
      update();
    });
}());
